import { useState, useCallback } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import {
  LocationHistoryExportResponse,
  exportLocationHistoryToCSV,
  exportLocationHistoryToXLSX,
} from '../utils/staffExportUtils';

const STAFF_LOCATION_HISTORY_EXPORT = gql`
  query StaffLocationHistoryExport($startDate: Date!, $endDate: Date!) {
    staffLocationHistoryExport(startDate: $startDate, endDate: $endDate) {
      hospitalName
      entries {
        date
        staffId
        staffName
        role
        roomId
        roomName
        roomType
        enteredAt
        exitedAt
        durationMinutes
        hourlyRate
        cost
      }
      summary {
        totalEntries
        totalCost
        totalHours
      }
    }
  }
`;

export type ExportFormat = 'csv' | 'xlsx';

interface UseStaffExportOptions {
  onError?: (error: Error) => void;
}

interface UseStaffExportReturn {
  exportLoading: boolean;
  exportMenuAnchor: HTMLElement | null;
  handleExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleExportClose: () => void;
  handleExport: (format: ExportFormat, startDate: string, endDate: string) => void;
}

export const useStaffExport = (options?: UseStaffExportOptions): UseStaffExportReturn => {
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingExportFormat, setPendingExportFormat] = useState<ExportFormat | null>(null);

  const [fetchExportData, { loading: exportLoading }] = useLazyQuery<LocationHistoryExportResponse>(
    STAFF_LOCATION_HISTORY_EXPORT,
    {
      fetchPolicy: 'network-only',
      onCompleted: exportData => {
        if (exportData?.staffLocationHistoryExport && pendingExportFormat) {
          if (pendingExportFormat === 'csv') {
            exportLocationHistoryToCSV(exportData.staffLocationHistoryExport);
          } else {
            exportLocationHistoryToXLSX(exportData.staffLocationHistoryExport);
          }
          setPendingExportFormat(null);
        }
      },
      onError: err => {
        console.error('Export failed:', err);
        setPendingExportFormat(null);
        if (options?.onError) {
          options.onError(err);
        } else {
          alert('Failed to export data. Please try again.');
        }
      },
    }
  );

  const handleExportClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleExport = useCallback(
    (format: ExportFormat, startDate: string, endDate: string) => {
      setExportMenuAnchor(null);
      setPendingExportFormat(format);

      fetchExportData({
        variables: {
          startDate,
          endDate,
        },
      });
    },
    [fetchExportData]
  );

  return {
    exportLoading,
    exportMenuAnchor,
    handleExportClick,
    handleExportClose,
    handleExport,
  };
};
